import React, { useMemo, useState } from "react";
import { Search, X, Plus, AlertTriangle } from "lucide-react";

const COMMON_LIGHTS = [
  "Check Engine", "ABS", "TPMS (Tire Pressure)", "Airbag / SRS",
  "Battery / Charging", "Oil Pressure", "Brake System", "Parking Brake",
  "Traction Control", "ESP / Stability", "Engine Temperature", "Glow Plug",
  "DPF", "AdBlue / DEF", "Transmission", "Immobilizer / Security",
  "Low Fuel", "Washer Fluid", "Service / Maintenance", "Power Steering",
];

/**
 * Multi-select picker for dashboard warning lights.
 * Value is a comma-joined string (backward-compatible with existing storage);
 * empty selection = no lights on.
 */
export default function WarningLightsPicker({ value, onChange, testid = "ci-info-lights" }) {
  const selected = useMemo(
    () => (value || "").split(",").map((s) => s.trim()).filter(Boolean),
    [value]
  );
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const q = query.trim().toLowerCase();
  const list = COMMON_LIGHTS.filter(
    (l) => !selected.includes(l) && (!q || l.toLowerCase().includes(q))
  );
  const canCustom = q && !COMMON_LIGHTS.some((l) => l.toLowerCase() === q) && !selected.some((s) => s.toLowerCase() === q);

  const save = (arr) => onChange(arr.join(", "));
  const add = (l) => { save([...selected, l]); setQuery(""); setOpen(false); };
  const remove = (l) => save(selected.filter((s) => s !== l));

  return (
    <div data-testid={testid}>
      {/* Selected chips */}
      <div className={`flex flex-wrap gap-2 ${selected.length ? "mb-2" : ""}`}>
        {selected.map((l) => (
          <span key={l} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/12 border border-amber-500/35 text-amber-300 text-xs"
            data-testid={`${testid}-chip-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <AlertTriangle className="w-3 h-3" />
            {l}
            <button type="button" onClick={() => remove(l)} className="hover:text-white" data-testid={`${testid}-rm-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      {/* Search / add */}
      <div className="relative">
        <div className="flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/12 rounded-lg focus-within:border-[#1F5EFF] transition-colors">
          <Search className="w-4 h-4 text-white/40 shrink-0" />
          <input
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); if (canCustom) add(query.trim()); else if (list.length) add(list[0]); }
              if (e.key === "Escape") setOpen(false);
            }}
            placeholder={selected.length ? "Add another light…" : "None on — or type to search…"}
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder-white/30"
            data-testid={`${testid}-search`}
          />
        </div>
        {open && (list.length > 0 || canCustom) && (
          <div className="absolute z-50 mt-1.5 w-full max-h-52 overflow-y-auto rounded-xl border border-white/12 bg-[#101318] shadow-2xl shadow-black/60" data-testid={`${testid}-menu`}>
            {canCustom && (
              <button type="button" onClick={() => { add(query.trim()); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-[#8db2ff] hover:bg-[#1F5EFF]/10 border-b border-white/8"
                data-testid={`${testid}-custom`}>
                <Plus className="w-3.5 h-3.5" /> Add “{query.trim()}”
              </button>
            )}
            {list.map((l) => (
              <button key={l} type="button"
                onClick={() => { add(l); }}
                onMouseDown={(e) => e.preventDefault()}
                className="w-full px-3 py-2.5 text-left text-sm text-white/80 hover:bg-white/5"
                data-testid={`${testid}-opt-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>
      {open && (
        <div
          className="fixed inset-0 z-40"
          style={{ cursor: "pointer" }}
          onClick={() => setOpen(false)}
          onTouchStart={() => setOpen(false)}
          data-testid={`${testid}-overlay`}
        />
      )}
    </div>
  );
}
